import { LoadingIndicator } from "@/Icons/loadingIndicator";
import { ExportButton } from "@/components/ExportButton";
import { Button } from "@/components/button/index";
import { useTrans } from "@/translations";

import React, { ComponentProps, FC, MutableRefObject, useMemo } from "react";
import { Link } from "react-router-dom";

import { twMerge } from "tailwind-merge";

import {
  ActiveOptions,
  FilterSortProvider,
} from "./filterSort/FilterSortContext";
import { FilterSorts } from "./filterSort/FilterSorts";
import { FilterSort } from "./filterSort/types";

export enum CardComponentVariant {
  DRAWER = "Drawer",
  STANDARD = "Standard",
}

export enum CardComponentFilterLocation {
  LEFT = "left",
  RIGHT = "right",
  INSIDE = "inside",
}

export type DisabledButtonInfo = {
  id: string;
  text: string;
};

export type LinkData = {
  to: string;
  label: string;
  isDisabled?: boolean;
  disabledMessage?: string;
};

export type ActionData = {
  label: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  disabledMessage?: string;
  onClick: () => void;
};

export type ToggleProperties = {
  label: string;
  toggled: boolean;
  onToggleChange: (checked: boolean) => void;
};

export enum ToggleViewLocation {
  AfterFilters = "after",
  BelowFilters = "below",
}

export interface ICardComponentProps {
  children?: React.ReactNode;
  title?: string;
  translateTitle?: boolean;
  filters?: FilterSort[];
  halfCard?: boolean;
  button?: ComponentProps<typeof Button>;
  customButton?: React.JSX.Element;
  exportButton?: ComponentProps<typeof ExportButton>;
  link?: LinkData;
  action?: ActionData;
  hasXContentPadding?: boolean;
  hasYContentPadding?: boolean;
  /** Use this when doing full height cards and you need to scroll inside them */
  overflow?: "none" | "auto";
  /** Used when saving filters in URL query params */
  onTitleClick?: () => void;
  variant?: CardComponentVariant;
  containerRef?: MutableRefObject<HTMLDivElement | null>;
  collapsed?: boolean;
  onFilterOptionChange?: (activeOptions: ActiveOptions<any>) => void;
  useSearchParams?: boolean;
  filterLocation?: CardComponentFilterLocation;
  alwaysExpanded?: boolean;
  className?: string;
  toggleView?: React.ReactNode;
  toggleViewLocation?: ToggleViewLocation;
  isLoading?: boolean;
}

export const Card: FC<ICardComponentProps> = ({
  children,
  title: _title = "",
  translateTitle = true,
  filters = [],
  halfCard,
  button,
  customButton,
  exportButton,
  link,
  action,
  hasXContentPadding = true,
  hasYContentPadding = true,
  overflow = "none",
  onTitleClick,
  variant = CardComponentVariant.STANDARD,
  containerRef,
  collapsed = false,
  onFilterOptionChange,
  useSearchParams,
  filterLocation = CardComponentFilterLocation.LEFT,
  alwaysExpanded = false,
  className,
  toggleView,
  toggleViewLocation = ToggleViewLocation.AfterFilters,
  isLoading = false,
}) => {
  const { t } = useTrans();

  const title = useMemo(
    () => (translateTitle ? t(_title) : _title),
    [translateTitle, t, _title],
  );

  return (
    <FilterSortProvider
      filterSorts={filters}
      onOptionChange={onFilterOptionChange}
      useSearchParams={useSearchParams}
    >
      <div
        // print:h-0 prevents a bug when printing QR codes inside MultiListTable
        className={twMerge([
          `relative flex w-full flex-col print:h-0`,
          !halfCard && "row-auto/2",
          className,
        ])}
        ref={containerRef}
      >
        <div
          className={twMerge(
            "border-grey1 flex w-full max-w-full items-center justify-between gap-4 border-b-2",
            variant === CardComponentVariant.STANDARD &&
              "bg-white px-4 py-3 sm:px-5",
            variant === CardComponentVariant.DRAWER &&
              "bg-lime-light px-8 py-2",
          )}
        >
          <div
            className={twMerge(
              "relative flex w-1 flex-1 shrink flex-row items-center",
              onTitleClick && "cursor-pointer",
              variant === CardComponentVariant.STANDARD && "my-1",
              variant === CardComponentVariant.DRAWER && "my-0",
            )}
            onClick={onTitleClick}
          >
            <div className="flex flex-1">
              {title && (
                <h2 className="flex flex-1 overflow-hidden pr-1 text-xl sm:text-2xl sm:text-ellipsis sm:whitespace-nowrap">
                  {title}
                </h2>
              )}

              <div
                className={twMerge(
                  "flex justify-start",
                  toggleViewLocation === ToggleViewLocation.BelowFilters &&
                    "flex-col items-end",
                )}
              >
                {!!filters.length &&
                  filterLocation !== CardComponentFilterLocation.INSIDE && (
                    <div
                      className={twMerge(
                        "flex w-fit min-w-[150px] shrink gap-4",
                        title && "pl-2.5",
                        !alwaysExpanded && !toggleView && "flex-1",
                      )}
                    >
                      <FilterSorts
                        {...{
                          filterSorts: filters,
                          collapsed,
                          alwaysExpanded,
                          filterLocation,
                        }}
                      />
                    </div>
                  )}

                <div className="flex">
                  {toggleView && (
                    <div className="mr-0 w-full">{toggleView}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {action && (
            <a
              className={twMerge(
                "font-dm-sans pr-4 text-base font-medium underline hover:cursor-pointer",
                action.isDisabled && "cursor-not-allowed opacity-50",
              )}
              onClick={action.isDisabled ? undefined : action.onClick}
            >
              {action.isLoading ? <LoadingIndicator /> : action.label}
            </a>
          )}
          {link && (
            <Link
              className={twMerge(
                "font-dm-sans pr-4 text-base font-medium underline",
                link.isDisabled && "cursor-not-allowed opacity-50",
              )}
              to={link.isDisabled ? "#" : link.to}
              title={link.isDisabled ? link.disabledMessage : ""}
            >
              {link.label}
            </Link>
          )}
          {customButton || (button && <Button {...button} />)}
          {exportButton && <ExportButton {...exportButton} />}
        </div>
        {isLoading ? (
          <div className="flex h-full items-center justify-center bg-white">
            <LoadingIndicator />
          </div>
        ) : (
          <div
            className={twMerge(
              "h-full flex-1 bg-white",
              hasXContentPadding && "px-4 sm:px-8",
              hasYContentPadding && "py-6",
              overflow === "auto" && "overflow-auto",
            )}
          >
            {!!filters.length &&
              filterLocation === CardComponentFilterLocation.INSIDE && (
                <div className={twMerge("mx-4 mt-4 mb-3 flex flex-1 gap-4")}>
                  <FilterSorts
                    {...{
                      filterSorts: filters,
                      collapsed,
                      alwaysExpanded,
                      filterLocation,
                    }}
                  />
                </div>
              )}
            {children}
          </div>
        )}
      </div>
    </FilterSortProvider>
  );
};
